import { useCallback, useEffect, useState } from 'react';
import type { ApiResponse } from '@vigilo/shared';
import { CROWD_API_BASE_URL, get, post, TOKEN_KEY } from '@vigilo/api-client';
import { useMockMode } from '@/shell/MockModeContext';
import { useApp } from '@/shell/AppContext';
import { MOCK_REPORT_JOBS, type MockReportJob, type ReportJobFormat, type ReportJobStatus } from '../data/mock';
import {
  CROWD_PAGINATION_DEFAULT_PAGE_SIZE,
  type CrowdPaginationEnvelope,
} from '../components/PaginationControls';

export interface ReportParameterDef {
  name: string;
  type: string;
  required: boolean;
}

export interface ReportTemplate {
  id: string;
  templateId: string;
  displayName: string;
  description: string;
  parameters: ReportParameterDef[];
  outputFormats: ReportJobFormat[];
  registeredAt?: string;
}

export interface ReportJob {
  id: string;
  jobId?: string;
  templateId: string;
  status: ReportJobStatus;
  format: ReportJobFormat;
  requestedAt: string;
  completedAt: string | null;
  outputHash: string | null;
  outputSizeBytes: number | null;
  errorMessage: string | null;
  parameters: Record<string, unknown>;
  downloadUrl?: string;
}

const MOCK_TEMPLATES: ReportTemplate[] = [
  {
    id: 'tmpl-zone-activity',
    templateId: 'zone_activity_summary',
    displayName: 'Zone Activity Summary',
    description: 'Occupancy over time, peak density, alert count, and alert timeline for a zone.',
    parameters: [
      { name: 'operationId', type: 'string', required: true },
      { name: 'zoneId', type: 'string', required: true },
      { name: 'from', type: 'ISO8601', required: true },
      { name: 'to', type: 'ISO8601', required: true },
    ],
    outputFormats: ['pdf', 'csv'],
  },
  {
    id: 'tmpl-crowd-trend',
    templateId: 'crowd_trend_report',
    displayName: 'Crowd Trend Report',
    description: 'Aggregate crowd load, zone breakdown, and alert summary.',
    parameters: [
      { name: 'operationId', type: 'string', required: true },
      { name: 'from', type: 'ISO8601', required: true },
      { name: 'to', type: 'ISO8601', required: true },
      { name: 'zoneIds', type: 'string[]', required: false },
    ],
    outputFormats: ['pdf'],
  },
  {
    id: 'tmpl-alert-history',
    templateId: 'alert_history_report',
    displayName: 'Alert History Report',
    description: 'All alerts in a period with lifecycle timestamps and resolution notes.',
    parameters: [
      { name: 'operationId', type: 'string', required: true },
      { name: 'from', type: 'ISO8601', required: true },
      { name: 'to', type: 'ISO8601', required: true },
      { name: 'zoneId', type: 'string', required: false },
    ],
    outputFormats: ['csv', 'pdf'],
  },
  {
    id: 'tmpl-operational',
    templateId: 'operational_summary',
    displayName: 'Operational Summary',
    description: 'Command briefing report covering event overview, peak occupancy, critical alerts, and key timeline.',
    parameters: [
      { name: 'operationId', type: 'string', required: true },
      { name: 'from', type: 'ISO8601', required: true },
      { name: 'to', type: 'ISO8601', required: true },
    ],
    outputFormats: ['pdf'],
  },
  {
    id: 'tmpl-flagged',
    templateId: 'flagged_device_activity_summary',
    displayName: 'Flagged Token Activity Summary',
    description: 'Anonymous movement log for a flagged token. No subscriber identity included.',
    parameters: [
      { name: 'operationId', type: 'string', required: true },
      { name: 'flagId', type: 'string', required: true },
    ],
    outputFormats: ['pdf'],
  },
  {
    id: 'tmpl-audit-log',
    templateId: 'audit_log_export',
    displayName: 'Audit Log Export',
    description: 'Filtered audit log with structured fields, review markers, and readable state changes.',
    parameters: [
      { name: 'operationId', type: 'string', required: true },
      { name: 'auditFilters', type: 'object', required: false },
    ],
    outputFormats: ['pdf', 'csv'],
  },
];

const CROWD_REPORT_TEMPLATE_IDS = new Set([
  'zone_activity_summary',
  'crowd_trend_report',
  'alert_history_report',
  'operational_summary',
  'flagged_device_activity_summary',
  'audit_log_export',
]);

function unwrap<T>(body: T | ApiResponse<T>): T {
  if (body && typeof body === 'object' && 'data' in body) return (body as ApiResponse<T>).data;
  return body as T;
}

function normalizeJob(job: ReportJob | (Omit<ReportJob, 'id'> & { jobId: string })): ReportJob {
  return {
    ...job,
    id: 'id' in job ? job.id : job.jobId,
    jobId: 'jobId' in job ? job.jobId : job.id,
    parameters: 'parameters' in job ? job.parameters : {},
  };
}

function isCrowdTemplate(templateId: string): boolean {
  return CROWD_REPORT_TEMPLATE_IDS.has(templateId);
}

function crowdTemplates(templates: ReportTemplate[]): ReportTemplate[] {
  return templates.filter(template => isCrowdTemplate(template.templateId));
}

function crowdJobs(jobs: ReportJob[]): ReportJob[] {
  return jobs.filter(job => isCrowdTemplate(job.templateId));
}

function operationJobs(jobs: ReportJob[], operationId: string | undefined): ReportJob[] {
  if (!operationId) return [];
  return jobs.filter(job => job.parameters?.['operationId'] === operationId);
}

function fromMockJob(job: MockReportJob): ReportJob {
  return { ...job, jobId: job.id };
}

function mockReadyJob(job: ReportJob): ReportJob {
  return {
    ...job,
    status: 'ready',
    completedAt: new Date().toISOString(),
    outputHash: 'c8b12240a4f29d31ef025e71aa30b81e9c2d771a92b5ac008ec5a024d91f2240',
    outputSizeBytes: job.format === 'csv' ? 28400 : 145230,
    errorMessage: null,
    downloadUrl: `/api/v1/reports/${job.id}/download`,
  };
}

function triggerDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function filenameFromDisposition(disposition: string | null, fallback: string): string {
  const match = disposition?.match(/filename="([^"]+)"/);
  return match?.[1] ?? fallback;
}

function escapePdfText(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

function mockPdfBlob(jobId: string, job: ReportJob | undefined): Blob {
  const title = job?.templateId?.replace(/_/g, ' ').toUpperCase() ?? 'VIGILO REPORT EXPORT';
  const generatedAt = new Date().toISOString();
  const lines = [
    'LocationGuru - Vigilo Crowd Monitoring',
    title,
    `Report ID: ${jobId}`,
    `Generated: ${generatedAt}`,
    'Mock-mode preview export. Live mode downloads the Core-rendered PDF.',
  ];
  const content = [
    'BT',
    '/F1 18 Tf',
    '50 760 Td',
    `(${escapePdfText(lines[0])}) Tj`,
    '/F1 12 Tf',
    ...lines.slice(1).map((line, index) => `50 ${720 - index * 22} Td (${escapePdfText(line)}) Tj`),
    'ET',
  ].join('\n');
  const objects = [
    '1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj',
    '2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj',
    '3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >> endobj',
    '4 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj',
    `5 0 obj << /Length ${content.length} >> stream\n${content}\nendstream endobj`,
  ];
  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  objects.forEach(object => {
    offsets.push(pdf.length);
    pdf += `${object}\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += '0000000000 65535 f \n';
  offsets.slice(1).forEach(offset => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
  return new Blob([pdf], { type: 'application/pdf' });
}

function mockCsvBlob(jobId: string, job: ReportJob | undefined): Blob {
  const rows = [
    ['report_id', 'template_id', 'generated_at'],
    [jobId, job?.templateId ?? 'report', new Date().toISOString()],
  ];
  return new Blob([rows.map(row => row.join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
}

type PaginatedJobs = { jobs: ReportJob[]; total: number; page: number; pageSize: number; hasNextPage: boolean };

function reportQuery(operationId: string, page: number, pageSize: number): string {
  const params = new URLSearchParams();
  params.set('operationId', operationId);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  return params.toString();
}

function pageMockJobs(jobs: ReportJob[], page: number, pageSize: number): PaginatedJobs {
  const offset = (page - 1) * pageSize;
  return {
    jobs: jobs.slice(offset, offset + pageSize),
    total: jobs.length,
    page,
    pageSize,
    hasNextPage: offset + pageSize < jobs.length,
  };
}

export function useReports() {
  const { isMock } = useMockMode();
  const { selectedOperation } = useApp();
  const [templates, setTemplates] = useState<ReportTemplate[]>(() => (isMock ? MOCK_TEMPLATES : []));
  const [jobs, setJobs] = useState<ReportJob[]>(() =>
    isMock ? operationJobs(MOCK_REPORT_JOBS.map(fromMockJob), selectedOperation?.id) : []
  );
  const [total, setTotal] = useState(() =>
    isMock ? operationJobs(MOCK_REPORT_JOBS.map(fromMockJob), selectedOperation?.id).length : 0
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeValue] = useState(CROWD_PAGINATION_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [selectedOperation?.id]);

  const refreshJobs = useCallback(async () => {
    if (isMock) {
      const result = pageMockJobs(operationJobs(MOCK_REPORT_JOBS.map(fromMockJob), selectedOperation?.id), page, pageSize);
      setJobs(result.jobs);
      setTotal(result.total);
      return;
    }
    if (!selectedOperation?.id) {
      setJobs([]);
      setTotal(0);
      return;
    }

    const qs = reportQuery(selectedOperation.id, page, pageSize);
    const body = await get<PaginatedJobs | ApiResponse<PaginatedJobs>>(`/reports?${qs}`);
    const result = unwrap(body);
    const scopedJobs = crowdJobs(result.jobs.map(normalizeJob));
    setJobs(scopedJobs);
    setTotal(result.total);
  }, [isMock, page, pageSize, selectedOperation?.id]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (isMock) {
        setTemplates(MOCK_TEMPLATES);
        const result = pageMockJobs(operationJobs(MOCK_REPORT_JOBS.map(fromMockJob), selectedOperation?.id), page, pageSize);
        setJobs(result.jobs);
        setTotal(result.total);
        return;
      }

      if (!selectedOperation?.id) {
        setTemplates([]);
        setJobs([]);
        setTotal(0);
        return;
      }

      setLoading(true);
      try {
        const qs = reportQuery(selectedOperation.id, page, pageSize);
        const [templateBody, jobBody] = await Promise.all([
          get<ReportTemplate[] | ApiResponse<ReportTemplate[]>>('/reports/templates'),
          get<PaginatedJobs | ApiResponse<PaginatedJobs>>(`/reports?${qs}`),
        ]);
        if (!cancelled) {
          setTemplates(crowdTemplates(unwrap(templateBody)));
          const result = unwrap(jobBody);
          const scopedJobs = crowdJobs(result.jobs.map(normalizeJob));
          setJobs(scopedJobs);
          setTotal(result.total);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [isMock, page, pageSize, selectedOperation?.id]);

  const requestReport = useCallback(async (
    templateId: string,
    format: ReportJobFormat,
    parameters: Record<string, unknown>,
  ): Promise<ReportJob> => {
    if (isMock) {
      const id = `rpt-job-${Date.now()}`;
      const job: ReportJob = {
        id,
        jobId: id,
        templateId,
        format,
        status: 'generating',
        requestedAt: new Date().toISOString(),
        completedAt: null,
        outputHash: null,
        outputSizeBytes: null,
        errorMessage: null,
        parameters,
      };
      setJobs(prev => [job, ...prev]);
      setTotal(prev => prev + 1);
      setPage(1);
      return job;
    }
    if (!isCrowdTemplate(templateId)) {
      throw new Error(`Report template ${templateId} is not available in Crowd Monitoring`);
    }

    // Reports print times in the requester's own zone, so tell the renderer which one that is.
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const body = await post<ReportJob | ApiResponse<ReportJob>>('/reports', {
      templateId,
      format,
      parameters: timeZone ? { timeZone, ...parameters } : parameters,
    });
    const job = normalizeJob(unwrap(body));
    setJobs(prev => [job, ...prev.filter(item => item.id !== job.id)]);
    setTotal(prev => prev + 1);
    setPage(1);
    return job;
  }, [isMock]);

  const pollJob = useCallback(async (jobId: string): Promise<ReportJob> => {
    if (isMock) {
      await new Promise(resolve => window.setTimeout(resolve, 1500));
      const finalJob = mockReadyJob(jobs.find(job => job.id === jobId) ?? {
        id: jobId,
        jobId,
        templateId: 'operational_summary',
        format: 'pdf',
        status: 'generating',
        requestedAt: new Date().toISOString(),
        completedAt: null,
        outputHash: null,
        outputSizeBytes: null,
        errorMessage: null,
        parameters: {},
      });
      setJobs(prev => prev.map(job => (job.id === jobId ? finalJob : job)));
      return finalJob;
    }

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          if (!selectedOperation?.id) throw new Error('operationId is required for Crowd report jobs');
          const body = await get<ReportJob | ApiResponse<ReportJob>>(
            `/reports/${jobId}?operationId=${encodeURIComponent(selectedOperation.id)}`,
          );
          const job = normalizeJob(unwrap(body));
          setJobs(prev => [job, ...prev.filter(item => item.id !== job.id)]);
          if (job.status === 'ready' || job.status === 'failed') {
            resolve(job);
            return;
          }
          window.setTimeout(() => { void poll(); }, 2000);
        } catch (error) {
          reject(error);
        }
      };
      void poll();
    });
  }, [isMock, jobs, selectedOperation?.id]);

  const downloadReport = useCallback(async (jobId: string): Promise<void> => {
    if (isMock) {
      await new Promise(resolve => window.setTimeout(resolve, 1500));
      const job = jobs.find(item => item.id === jobId);
      const templateId = job?.templateId ?? 'report';
      if (job?.format === 'csv') {
        triggerDownload(`vigilo-${templateId}-${jobId}.csv`, mockCsvBlob(jobId, job));
        return;
      }
      triggerDownload(`vigilo-${templateId}-${jobId}.pdf`, mockPdfBlob(jobId, job));
      return;
    }

    const token = localStorage.getItem(TOKEN_KEY());
    if (!selectedOperation?.id) throw new Error('operationId is required for Crowd report jobs');
    const response = await fetch(
      `${CROWD_API_BASE_URL}/reports/${jobId}/download?operationId=${encodeURIComponent(selectedOperation.id)}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      },
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const contentType = response.headers.get('Content-Type') ?? '';
    if (contentType.includes('application/json')) {
      const body = await response.text();
      throw new Error(`Expected report file, received JSON: ${body}`);
    }
    const blob = await response.blob();
    const ext = contentType.includes('text/csv') ? 'csv' : 'pdf';
    const filename = filenameFromDisposition(response.headers.get('Content-Disposition'), `vigilo-report-${jobId}.${ext}`);
    triggerDownload(filename, blob);
  }, [isMock, jobs, selectedOperation?.id]);

  const setJobPage = useCallback((nextPage: number) => {
    setPage(Math.max(1, Math.trunc(nextPage)));
  }, []);

  const setJobPageSize = useCallback((nextPageSize: number) => {
    setPageSizeValue(Math.max(1, Math.trunc(nextPageSize)));
    setPage(1);
  }, []);

  const pagination: CrowdPaginationEnvelope = {
    total,
    page,
    pageSize,
    hasNextPage: page * pageSize < total,
  };

  return {
    templates,
    jobs,
    total,
    pagination,
    loading,
    requestReport,
    pollJob,
    downloadReport,
    refreshJobs,
    setPage: setJobPage,
    setPageSize: setJobPageSize,
  };
}
